// Recovery flow for the "Auth session missing" needs-login state.
//
// 1. Try a silent supabase.auth.refreshSession() — common case where the
//    WebView still has a refresh token, the session was just stale.
//    On success, optionally gate behind Face ID (when biometric unlock is
//    enabled) then resume the drain.
// 2. If the silent refresh fails, send the user to /login with a returnTo
//    pointer so they land back on /offline-control after sign-in.
//
// OTA-safe: pure JS. Biometric module is the existing approved native plugin.

import { supabase } from "@/integrations/supabase/client";
import { clearNeedsLogin, drainPingOutbox, getOutboxStats } from "@/lib/native/ping-outbox";
import { isBiometricUnlockEnabled, verifyBiometric } from "@/lib/native/biometric";
import { toast } from "sonner";

const RETURN_TO_KEY = "roava.auth.returnTo";

export type ResumeNavigate = (path: string) => void;

export type ResumeOutcome =
  | "resumed"        // silent refresh + (optional) Face ID succeeded, drain kicked
  | "biometric_cancelled"
  | "needs_login";   // refresh failed, navigated to /login

export async function resumeSyncFlow(navigate: ResumeNavigate): Promise<ResumeOutcome> {
  // Step 1: silent refresh — direct call, NOT via refreshSessionAndSync, so
  // we don't recurse through outbox state.
  let refreshOk = false;
  try {
    const { data, error } = await supabase.auth.refreshSession();
    refreshOk = !error && !!data?.session;
  } catch {
    refreshOk = false;
  }

  if (refreshOk) {
    // Optional Face ID gate.
    try {
      if (await isBiometricUnlockEnabled()) {
        try {
          await verifyBiometric("Resume offline sync");
        } catch {
          return "biometric_cancelled";
        }
      }
    } catch { /* biometric status threw — proceed without gate */ }

    clearNeedsLogin();
    const queued = getOutboxStats().queued;
    void drainPingOutbox({ userInitiated: true }).catch(() => {});
    toast.success(
      queued > 0
        ? `Reconnected — syncing ${queued.toLocaleString()} ping${queued === 1 ? "" : "s"}…`
        : "Reconnected.",
    );
    return "resumed";
  }

  // Step 2: full sign-in. Stash returnTo so /login can route us back.
  try {
    if (typeof sessionStorage !== "undefined") {
      sessionStorage.setItem(RETURN_TO_KEY, "/offline-control");
    }
  } catch { /* ignore */ }
  navigate("/login");
  return "needs_login";
}

/** Read + consume the returnTo pointer set by resumeSyncFlow. */
export function consumeAuthReturnTo(): string | null {
  try {
    if (typeof sessionStorage === "undefined") return null;
    const v = sessionStorage.getItem(RETURN_TO_KEY);
    if (v) sessionStorage.removeItem(RETURN_TO_KEY);
    if (!v || !v.startsWith("/") || v.startsWith("//")) return null;
    return v;
  } catch {
    return null;
  }
}
